import React, { useEffect, useRef, useState } from 'react';
import {
  DIAS_UTEIS,
  DIA_LABEL,
  SHIFT_WINDOWS,
  eventMarkerPercent,
  formatSimDateBadge,
  timelineProgress,
  type DiaSemanaKey,
  type QueueControl,
  type ShiftDayPlan,
  type ShiftKey,
  type SimEngineMode,
  type SimEvent,
} from '../sim/simDayTemplates';
import type { SimPresenceMix } from '../sim/simPresenceMix';

export type SchoolTurnoOpt = { id: string; nome: string; shift: ShiftKey };

export type SchoolSimTelemetryStripProps = {
  schoolNome: string;
  expanded: boolean;
  onToggle: () => void;
  virtualTime: Date;
  simulationSpeed: number;
  setSimulationSpeed: (n: number) => void;
  isSimulationPlaying: boolean;
  setIsSimulationPlaying: (v: boolean | ((prev: boolean) => boolean)) => void;
  logs: string[];
  onClearLogs: () => void;
  readerOnline: boolean | null;
  busy: 'rfid' | 'chamada' | null;
  activeShift: ShiftKey;
  onShiftChange: (s: ShiftKey) => void;
  turnosDisponiveis: SchoolTurnoOpt[];
  eventQueue: SimEvent[];
  queueControl: QueueControl;
  onQueueControl: (c: QueueControl) => void;
  onRunNext: () => void;
  onSkipNext: () => void;
  simMode: SimEngineMode;
  chronosBusy: boolean;
  chronosClock?: string | null;
  chronosFase?: string | null;
  chronosPaused?: boolean;
  onPrepare: () => void;
  onCleanToday: () => void;
  onChronosStart: () => void;
  onChronosPauseResume: () => void;
  onChronosStop: () => void;
  /** Atalhos manuais (antes no header da janela). */
  onManualRfid?: () => void;
  onManualChamada?: () => void;
  simDateYmd: string;
  simDiaSemana: DiaSemanaKey;
  onSimDateChange: (ymd: string) => void;
  onSimDiaChange: (dia: DiaSemanaKey) => void;
  /** Fim da Noite → próximo dia letivo e recomeça M/T/N sem parar. */
  dayLoopEnabled: boolean;
  onDayLoopChange: (enabled: boolean) => void;
  dayPlan: ShiftDayPlan | null;
  /** Mix operacional: RFID / sem RFID / ausentes (soma 100). */
  presenceMix: SimPresenceMix;
  onPresenceMixChange: (mix: SimPresenceMix) => void;
  /** Aplica hora virtual (HH:mm ou HH:mm:ss) no dia sim atual e sincroniza embeds. */
  onVirtualTimeChange?: (hm: string) => void;
};

function logColorClass(log: string): string {
  if (log.includes('[RFID]')) return 'text-[#cba6f7]';
  if (log.includes('[Chamada]')) return 'text-[#89b4fa]';
  if (log.includes('[Comunicado]')) return 'text-[#f9e2af]';
  if (log.includes('[Chronos]')) return 'text-[#94e2d5]';
  if (log.includes('[Recreio]')) return 'text-[#a6e3a1]';
  if (/falha|erro/i.test(log)) return 'text-[#f38ba8]';
  return 'text-[#a6adc8]';
}

function formatVirtualClock(d: Date): string {
  return (
    `${String(d.getHours()).padStart(2, '0')}:` +
    `${String(d.getMinutes()).padStart(2, '0')}:` +
    `${String(d.getSeconds()).padStart(2, '0')}`
  );
}

/** Aceita HH:mm, HH:mm:ss ou HHmm — normaliza para HH:mm:ss. */
function normalizeClockInput(raw: string): string | null {
  const t = raw.trim().replace(/\./g, ':');
  const m = t.match(/^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/) || t.match(/^(\d{2})(\d{2})(\d{2})?$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  const s = m[3] != null && m[3] !== '' ? Number(m[3]) : 0;
  if (![h, min, s].every((n) => Number.isFinite(n))) return null;
  if (h < 0 || h > 23 || min < 0 || min > 59 || s < 0 || s > 59) return null;
  return (
    `${String(h).padStart(2, '0')}:` +
    `${String(min).padStart(2, '0')}:` +
    `${String(s).padStart(2, '0')}`
  );
}

function markerColor(kind: SimEvent['kind'], status: SimEvent['status']): string {
  if (status === 'done') return 'bg-[#a6e3a1]';
  if (status === 'running') return 'bg-[#f9e2af]';
  if (status === 'skipped') return 'bg-[#585b70]';
  if (kind === 'recreio') return 'bg-[#a6e3a1]/70';
  if (kind === 'rfid' || kind === 'rfid_saida') return 'bg-[#cba6f7]';
  if (kind === 'comunicado') return 'bg-[#f9e2af]';
  return 'bg-[#89b4fa]';
}

const SHIFT_CHIPS: ShiftKey[] = ['MANHA', 'TARDE', 'NOITE'];
const SPEED_PRESETS = [1, 60, 240] as const;
const SHIFT_SHORT: Record<ShiftKey, string> = { MANHA: 'M', TARDE: 'T', NOITE: 'N' };

const btnBase =
  'px-1.5 py-0.5 rounded text-[8px] font-bold border cursor-pointer shrink-0 disabled:opacity-40 disabled:cursor-not-allowed';

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[7px] uppercase tracking-wider text-[#585b70] font-bold shrink-0">
      {children}
    </span>
  );
}

export function SchoolSimTelemetryStrip({
  schoolNome,
  expanded,
  onToggle,
  virtualTime,
  simulationSpeed,
  setSimulationSpeed,
  isSimulationPlaying,
  setIsSimulationPlaying,
  logs,
  onClearLogs,
  readerOnline,
  busy,
  activeShift,
  onShiftChange,
  turnosDisponiveis,
  eventQueue,
  queueControl,
  onQueueControl,
  onRunNext,
  onSkipNext,
  simMode,
  chronosBusy,
  chronosClock,
  chronosFase,
  chronosPaused,
  onPrepare,
  onCleanToday,
  onChronosStart,
  onChronosPauseResume,
  onChronosStop,
  onManualRfid,
  onManualChamada,
  simDateYmd,
  simDiaSemana,
  onSimDateChange,
  onSimDiaChange,
  dayLoopEnabled,
  onDayLoopChange,
  dayPlan,
  presenceMix,
  onPresenceMixChange,
  onVirtualTimeChange,
}: SchoolSimTelemetryStripProps) {
  const logEndRef = useRef<HTMLDivElement>(null);
  const clockInputRef = useRef<HTMLInputElement>(null);
  const clockCancelRef = useRef(false);
  const [opsOpen, setOpsOpen] = useState(false);
  const [clockEditing, setClockEditing] = useState(false);
  const [clockDraft, setClockDraft] = useState('');
  const [clockError, setClockError] = useState(false);

  const setMixField = (field: keyof SimPresenceMix, raw: string) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    const next = { ...presenceMix, [field]: Math.max(0, Math.min(100, Math.round(n))) };
    if (field === 'pctRfid' || field === 'pctSemRfid') {
      next.pctAusente = Math.max(0, 100 - next.pctRfid - next.pctSemRfid);
    } else {
      const room = Math.max(0, 100 - next.pctAusente);
      if (next.pctRfid > room) {
        next.pctRfid = room;
        next.pctSemRfid = 0;
      } else if (next.pctRfid + next.pctSemRfid > room) {
        next.pctSemRfid = room - next.pctRfid;
      }
    }
    onPresenceMixChange(next);
  };
  const recentLogs = logs.slice(-30);
  const lastLog = logs.length > 0 ? logs[logs.length - 1] : '';
  const lastIsError = /falha|erro/i.test(lastLog);
  const startHm = dayPlan?.boundaries?.abertura || SHIFT_WINDOWS[activeShift].startHm;
  const endHm = dayPlan?.boundaries?.fechamento || SHIFT_WINDOWS[activeShift].endHm;
  const progressPct = timelineProgress(virtualTime, startHm, endHm) * 100;
  const hasTurnoForShift = turnosDisponiveis.some((t) => t.shift === activeShift);
  const dateBadge = formatSimDateBadge(simDateYmd, simDiaSemana);
  const pendingCount = eventQueue.filter((e) => e.status === 'pending').length;
  const chronosActive = simMode === 'CHRONOS';

  useEffect(() => {
    if (expanded && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [expanded, logs.length]);

  useEffect(() => {
    if (!clockEditing) return;
    const id = window.setTimeout(() => {
      clockInputRef.current?.focus();
      clockInputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(id);
  }, [clockEditing]);

  const beginClockEdit = () => {
    if (chronosActive || !onVirtualTimeChange) return;
    setClockDraft(formatVirtualClock(virtualTime).slice(0, 5)); // HH:mm
    setClockError(false);
    setClockEditing(true);
  };

  const cancelClockEdit = () => {
    clockCancelRef.current = true;
    setClockEditing(false);
    setClockError(false);
  };

  const commitClockEdit = () => {
    if (clockCancelRef.current) {
      clockCancelRef.current = false;
      return;
    }
    if (!onVirtualTimeChange) {
      setClockEditing(false);
      return;
    }
    const normalized = normalizeClockInput(clockDraft);
    if (!normalized) {
      setClockError(true);
      return;
    }
    onVirtualTimeChange(normalized);
    setClockEditing(false);
    setClockError(false);
  };

  if (!expanded) {
    return (
      <button
        type="button"
        data-tour-sim="sim.telemetry"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-2 py-0.5 bg-[#11111b]/80 border-b border-[#313244]/60 text-left cursor-pointer hover:bg-[#181825] flex-shrink-0"
        title="Expandir acompanhamento da simulação"
      >
        <span className="text-[7px] font-bold uppercase text-[#cba6f7] shrink-0">
          {dateBadge} · {activeShift}
        </span>
        <span
          className={`text-[8px] font-mono truncate flex-1 min-w-0 ${
            lastIsError ? 'text-[#f38ba8]' : 'text-[#585b70]'
          }`}
        >
          {lastLog || 'Pausado — Play ou Preparar para começar'}
        </span>
        {chronosActive && (
          <span className="text-[7px] font-bold text-[#94e2d5] shrink-0">CHRONOS</span>
        )}
        {lastIsError && (
          <span className="text-[7px] font-bold uppercase text-[#f38ba8] border border-[#f38ba8]/40 px-1 rounded shrink-0">
            erro
          </span>
        )}
        {busy && (
          <span className="text-[7px] font-bold uppercase text-[#f9e2af] shrink-0">{busy}…</span>
        )}
        <span className="text-[9px] text-[#a6adc8] shrink-0">▾</span>
      </button>
    );
  }

  return (
    <div
      data-tour-sim="sim.telemetry"
      className="flex-shrink-0 border-b border-[#313244] bg-[#11111b]/95 px-2 py-1 flex gap-2 max-h-[200px] min-h-[132px]"
      onMouseDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {/* Controles ~58% (mín. 480px) — log fica à direita sem esmagar os chips */}
      <div className="flex-[58] min-w-[min(100%,480px)] max-w-[640px] flex flex-col gap-1 overflow-y-auto overflow-x-hidden">
        <div className="flex items-center gap-1 flex-nowrap">
          <button
            type="button"
            onClick={() => setIsSimulationPlaying((p) => !p)}
            disabled={chronosActive}
            className={`${btnBase} ${
              isSimulationPlaying
                ? 'bg-amber-500/20 text-amber-400 border-amber-500/40'
                : 'bg-green-500/25 text-green-400 border-green-500/40'
            }`}
            title={chronosActive ? 'Relógio do Chronos' : 'Iniciar / pausar tempo virtual'}
          >
            {isSimulationPlaying ? '⏸' : '▶'}
          </button>
          {clockEditing ? (
            <input
              ref={clockInputRef}
              type="text"
              inputMode="numeric"
              value={clockDraft}
              onChange={(e) => {
                setClockDraft(e.target.value);
                setClockError(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitClockEdit();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelClockEdit();
                }
              }}
              onBlur={() => {
                // Aplica ao sair do campo (Enter já aplica; Escape cancela antes do blur útil)
                if (clockEditing) commitClockEdit();
              }}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
              className={`font-mono font-bold text-[11px] text-white bg-[#1e1e2e] px-1.5 py-0.5 rounded border tabular-nums shrink-0 w-[5.5rem] outline-none ${
                clockError ? 'border-[#f38ba8]' : 'border-[#cba6f7]'
              }`}
              title="HH:mm ou HH:mm:ss — Enter aplica · Esc cancela"
              placeholder="HH:mm"
              aria-label="Editar hora virtual"
            />
          ) : (
            <button
              type="button"
              onDoubleClick={(e) => {
                e.stopPropagation();
                beginClockEdit();
              }}
              disabled={chronosActive || !onVirtualTimeChange}
              className="font-mono font-bold text-[11px] text-white bg-[#181825] px-1.5 py-0.5 rounded border border-[#313244] tabular-nums shrink-0 cursor-text disabled:cursor-default"
              title={
                chronosActive
                  ? 'Relógio do Chronos (não editável)'
                  : 'Duplo clique para editar a hora virtual (ex.: 07:55)'
              }
            >
              {chronosActive && chronosClock ? chronosClock : formatVirtualClock(virtualTime)}
            </button>
          )}
          <span
            className="text-[7px] font-bold text-[#89b4fa] border border-[#89b4fa]/35 bg-[#89b4fa]/10 px-1 py-0.5 rounded shrink-0"
            title="Dia da simulação"
          >
            {dateBadge}
          </span>
          {SPEED_PRESETS.map((sp) => (
            <button
              key={sp}
              type="button"
              disabled={chronosActive}
              onClick={() => setSimulationSpeed(sp)}
              className={`${btnBase} ${
                simulationSpeed === sp
                  ? 'bg-[#cba6f7]/25 text-[#cba6f7] border-[#cba6f7]/50'
                  : 'bg-[#181825] text-[#6c7086] border-[#313244]'
              }`}
            >
              {sp}x
            </button>
          ))}
          <button
            type="button"
            onClick={onPrepare}
            disabled={chronosBusy}
            className={`${btnBase} text-[#f9e2af] border-[#f9e2af]/40 bg-[#f9e2af]/15`}
            title="Limpa o dia sim e monta a fila do turno"
          >
            Preparar
          </button>
          <button
            type="button"
            onClick={() => setOpsOpen((o) => !o)}
            className={`${btnBase} text-[#a6adc8] border-[#585b70]/40 bg-[#181825]`}
            aria-expanded={opsOpen}
          >
            Ops {opsOpen ? '▴' : '▾'}
          </button>
          {chronosActive && (
            <span className="text-[7px] font-bold text-[#94e2d5] shrink-0">
              CHRONOS{chronosFase ? ` · ${chronosFase}` : ''}
            </span>
          )}
          <button
            type="button"
            onClick={onToggle}
            className={`${btnBase} text-[#a6adc8] border-[#313244] bg-[#181825] ml-auto`}
            title="Recolher"
          >
            ▴
          </button>
        </div>

        {/* Uma linha: cenário + fila (sem grade estreita que forçava quebra) */}
        <div className="rounded border border-[#313244]/80 bg-[#181825]/40 px-1.5 py-1 flex items-center gap-2 flex-nowrap min-w-0">
          <div className="flex items-center gap-1 shrink-0">
            <SectionLabel>Turno</SectionLabel>
            {SHIFT_CHIPS.map((s) => {
              const available = turnosDisponiveis.some((t) => t.shift === s);
              const active = activeShift === s;
              return (
                <button
                  key={s}
                  type="button"
                  disabled={chronosBusy && chronosActive}
                  onClick={() => onShiftChange(s)}
                  className={`${btnBase} ${
                    active
                      ? 'bg-[#cba6f7]/25 text-[#cba6f7] border-[#cba6f7]/50'
                      : available
                        ? 'bg-[#313244] text-[#cdd6f4] border-[#585b70]/40'
                        : 'bg-[#11111b] text-[#45475a] border-[#313244]'
                  }`}
                  title={available ? SHIFT_WINDOWS[s].label : `${SHIFT_WINDOWS[s].label} (sem turno)`}
                >
                  {SHIFT_SHORT[s]}
                </button>
              );
            })}
            <span
              className={`text-[7px] font-bold ${
                readerOnline
                  ? 'text-[#a6e3a1]'
                  : readerOnline === false
                    ? 'text-[#f38ba8]'
                    : 'text-[#585b70]'
              }`}
              title={schoolNome}
            >
              RFID {readerOnline ? 'OK' : readerOnline === false ? 'OFF' : '…'}
            </span>
          </div>

          <div className="w-px h-4 bg-[#313244] shrink-0" />

          <div className="flex items-center gap-0.5 shrink-0">
            <SectionLabel>Dia</SectionLabel>
            <input
              type="date"
              value={simDateYmd}
              onChange={(e) => onSimDateChange(e.target.value)}
              className="bg-[#11111b] border border-[#313244] rounded text-[7px] text-[#cdd6f4] px-0.5 py-0.5 outline-none w-[7.5rem]"
            />
            {DIAS_UTEIS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => onSimDiaChange(d)}
                className={`${btnBase} ${
                  simDiaSemana === d
                    ? 'bg-[#89b4fa]/25 text-[#89b4fa] border-[#89b4fa]/50'
                    : 'bg-[#313244] text-[#a6adc8] border-[#585b70]/30'
                }`}
              >
                {DIA_LABEL[d]}
              </button>
            ))}
            {dayPlan?.diaForaDoTurno && (
              <span className="text-[7px] text-[#f9e2af]">fora</span>
            )}
            <button
              type="button"
              onClick={() => onDayLoopChange(!dayLoopEnabled)}
              className={`${btnBase} ${
                dayLoopEnabled
                  ? 'bg-[#a6e3a1]/20 text-[#a6e3a1] border-[#a6e3a1]/45'
                  : 'bg-[#313244] text-[#a6adc8] border-[#585b70]/30'
              }`}
              title="Ao fim da Noite, avança para o próximo dia letivo (pula Sáb/Dom) e recomeça os turnos"
            >
              Loop
            </button>
          </div>

          <div className="w-px h-4 bg-[#313244] shrink-0" />

          <div className="flex items-center gap-0.5 shrink-0">
            <SectionLabel>Fila</SectionLabel>
            <button
              type="button"
              disabled={chronosActive}
              onClick={() => onQueueControl('manual')}
              className={`${btnBase} ${
                queueControl === 'manual'
                  ? 'bg-[#89b4fa]/25 text-[#89b4fa] border-[#89b4fa]/50'
                  : 'bg-[#313244] text-[#a6adc8] border-[#585b70]/30'
              }`}
              title="Só dispara com Prox"
            >
              Man
            </button>
            <button
              type="button"
              disabled={chronosActive}
              onClick={() => onQueueControl('auto')}
              className={`${btnBase} ${
                queueControl === 'auto'
                  ? 'bg-[#a6e3a1]/20 text-[#a6e3a1] border-[#a6e3a1]/45'
                  : 'bg-[#313244] text-[#a6adc8] border-[#585b70]/30'
              }`}
              title="Dispara no horário virtual (com Play)"
            >
              Auto
            </button>
            <button
              type="button"
              disabled={chronosActive}
              onClick={onRunNext}
              className={`${btnBase} text-[#a6e3a1] border-[#a6e3a1]/35 bg-[#a6e3a1]/10`}
            >
              ▶ Prox
            </button>
            <button
              type="button"
              disabled={chronosActive}
              onClick={onSkipNext}
              className={`${btnBase} text-[#a6adc8] border-[#585b70]/40`}
            >
              Pular
            </button>
            <span className="text-[7px] text-[#585b70] tabular-nums">
              {pendingCount}/{eventQueue.length}
              {dayPlan?.roster
                ? ` · ${dayPlan.roster.alunosComTag}t/${dayPlan.roster.professores}p`
                : ''}
            </span>
          </div>
        </div>

        <div className="relative h-4 mx-0.5">
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 rounded bg-[#313244]" />
          <div
            className="absolute top-1/2 -translate-y-1/2 h-1 rounded bg-[#cba6f7]/60"
            style={{ width: `${progressPct}%` }}
          />
          {eventQueue.map((ev) => (
            <div
              key={ev.id}
              className={`absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full border border-[#11111b] ${markerColor(ev.kind, ev.status)}`}
              style={{ left: `calc(${eventMarkerPercent(ev.at, startHm, endHm)}% - 3px)` }}
              title={`${ev.at} ${ev.label} (${ev.status})`}
            />
          ))}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-[#cdd6f4]"
            style={{ left: `${progressPct}%` }}
            title="Agora (virtual)"
          />
          <div className="absolute -bottom-0.5 left-0 text-[6px] text-[#585b70] font-mono">{startHm}</div>
          <div className="absolute -bottom-0.5 right-0 text-[6px] text-[#585b70] font-mono">{endHm}</div>
        </div>

        {opsOpen && (
          <div className="rounded border border-[#313244] bg-[#11111b] px-1.5 py-1 flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <SectionLabel>Mix turno</SectionLabel>
              <label className="flex items-center gap-0.5 text-[7px] text-[#a6adc8] font-bold">
                RFID%
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={presenceMix.pctRfid}
                  onChange={(e) => setMixField('pctRfid', e.target.value)}
                  className="w-9 bg-[#181825] border border-[#313244] rounded text-[8px] text-[#cba6f7] px-0.5 py-0.5 outline-none tabular-nums"
                  title="% do turno que entra com RFID na portaria"
                />
              </label>
              <label className="flex items-center gap-0.5 text-[7px] text-[#a6adc8] font-bold">
                s/RFID%
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={presenceMix.pctSemRfid}
                  onChange={(e) => setMixField('pctSemRfid', e.target.value)}
                  className="w-9 bg-[#181825] border border-[#313244] rounded text-[8px] text-[#89b4fa] px-0.5 py-0.5 outline-none tabular-nums"
                  title="% presentes na chamada sem leitura RFID"
                />
              </label>
              <label className="flex items-center gap-0.5 text-[7px] text-[#a6adc8] font-bold">
                Aus.%
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={presenceMix.pctAusente}
                  onChange={(e) => setMixField('pctAusente', e.target.value)}
                  className="w-9 bg-[#181825] border border-[#313244] rounded text-[8px] text-[#f38ba8] px-0.5 py-0.5 outline-none tabular-nums"
                  title="% ausentes (não vieram) — restante do mix"
                />
              </label>
              <span className="text-[7px] text-[#585b70] tabular-nums">
                = {presenceMix.pctRfid + presenceMix.pctSemRfid + presenceMix.pctAusente}%
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1">
            {onManualRfid && (
              <button
                type="button"
                onClick={onManualRfid}
                disabled={!!busy || chronosBusy}
                className={`${btnBase} text-[#a6e3a1] border-[#a6e3a1]/35 bg-[#a6e3a1]/10`}
                title="Disparar entrada RFID agora (atalho)"
              >
                {busy === 'rfid' ? '…' : 'Entrada'}
              </button>
            )}
            {onManualChamada && (
              <button
                type="button"
                onClick={onManualChamada}
                disabled={!!busy || chronosBusy}
                className={`${btnBase} text-[#89b4fa] border-[#89b4fa]/35 bg-[#89b4fa]/10`}
                title="Disparar chamada agora (atalho)"
              >
                {busy === 'chamada' ? '…' : 'Chamada'}
              </button>
            )}
            <button
              type="button"
              onClick={onCleanToday}
              disabled={chronosBusy}
              className={`${btnBase} text-[#f38ba8] border-[#f38ba8]/30 bg-[#f38ba8]/10`}
            >
              Limpar dia
            </button>
            {!chronosActive ? (
              <button
                type="button"
                onClick={onChronosStart}
                disabled={chronosBusy || !hasTurnoForShift}
                className={`${btnBase} text-[#94e2d5] border-[#94e2d5]/40 bg-[#94e2d5]/10`}
                title="Engine server-side (pausa a fila do cockpit)"
              >
                Chronos
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onChronosPauseResume}
                  disabled={chronosBusy}
                  className={`${btnBase} text-[#f9e2af] border-[#f9e2af]/30`}
                >
                  {chronosPaused ? 'Retomar' : 'Pausar'}
                </button>
                <button
                  type="button"
                  onClick={onChronosStop}
                  disabled={chronosBusy}
                  className={`${btnBase} text-[#f38ba8] border-[#f38ba8]/40`}
                >
                  Parar
                </button>
              </>
            )}
            <input
              type="range"
              min={1}
              max={240}
              value={simulationSpeed}
              onChange={(e) => setSimulationSpeed(Number(e.target.value))}
              disabled={chronosActive}
              className="w-16 h-1 bg-[#313244] rounded-lg appearance-none cursor-pointer accent-[#cba6f7] disabled:opacity-40"
              title={`Velocidade ${simulationSpeed}x`}
            />
            <button
              type="button"
              disabled={chronosActive}
              onClick={() =>
                onQueueControl(queueControl === 'pause_next' ? 'auto' : 'pause_next')
              }
              className={`${btnBase} ${
                queueControl === 'pause_next'
                  ? 'text-[#f9e2af] border-[#f9e2af]/40'
                  : 'text-[#a6adc8] border-[#585b70]/30'
              }`}
              title="Pausa antes do próximo evento"
            >
              Pausar próx.
            </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-0.5 border-l border-[#313244]/80 pl-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[8px] font-bold uppercase tracking-wider text-[#a6adc8]">
            Log ao vivo
          </span>
          <button
            type="button"
            onClick={onClearLogs}
            className="text-[8px] text-[#a6adc8]/60 hover:text-[#cdd6f4] bg-transparent border-none cursor-pointer"
          >
            Limpar
          </button>
        </div>
        <div className="bg-[#181825] border border-[#313244] rounded-lg px-2 py-1 flex-1 min-h-0 overflow-y-auto font-mono text-[8px] flex flex-col gap-0.5 scrollbar-thin select-text">
          {recentLogs.length === 0 ? (
            <div className="opacity-40 italic text-center py-2">
              Sem atividades — Preparar → Play ou Prox
            </div>
          ) : (
            recentLogs.map((log, idx) => (
              <div key={`${idx}-${log.slice(0, 24)}`} className={logColorClass(log)}>
                {log}
              </div>
            ))
          )}
          <div ref={logEndRef} />
        </div>
      </div>
    </div>
  );
}

export function SchoolSimStripToggle({
  expanded,
  onToggle,
  hasError,
}: {
  expanded: boolean;
  onToggle: () => void;
  hasError?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`px-1.5 py-0.5 rounded-full bg-[#313244] text-[8px] font-bold border cursor-pointer hover:bg-[#45475a] ${
        hasError ? 'text-[#f38ba8] border-[#f38ba8]/40' : 'text-[#cdd6f4] border-[#585b70]/40'
      }`}
      title={expanded ? 'Recolher telemetria' : 'Expandir acompanhamento'}
    >
      {expanded ? '▴' : '▾'}
    </button>
  );
}
